import React from "react";

/**
 * Loading spinner — branded to match the dark/orange UI theme.
 *
 * Props:
 *   size    — "sm" | "md" (default) | "lg"
 *   inline  — if true, renders without the centering wrapper (use inside buttons)
 *   label   — accessible screen-reader label (default "Loading…")
 */
const Loading = ({ size = "md", inline = false, label = "Loading…" }) => {
  const dims       = { sm: 18, md: 40, lg: 56 }[size]    ?? 40;
  const stroke     = { sm: 2.5, md: 3, lg: 3.5 }[size]   ?? 3;
  const r          = dims / 2 - stroke;
  const circ       = 2 * Math.PI * r;

  const spinner = (
    <span
      role="status"
      aria-label={label}
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: dims,
        height: dims,
        flexShrink: 0,
      }}
    >
      <svg
        width={dims}
        height={dims}
        viewBox={`0 0 ${dims} ${dims}`}
        fill="none"
        style={{ animation: "loading-spin 0.9s linear infinite" }}
      >
        {/* Track ring */}
        <circle
          cx={dims / 2}
          cy={dims / 2}
          r={r}
          stroke="rgba(218,123,48,0.15)"
          strokeWidth={stroke}
        />
        {/* Gradient arc — 72% of the circumference */}
        <circle
          cx={dims / 2}
          cy={dims / 2}
          r={r}
          stroke="url(#lg-spin)"
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={`${circ * 0.72} ${circ * 0.28}`}
        />
        <defs>
          <linearGradient id="lg-spin" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%"   stopColor="#DA7B30" />
            <stop offset="100%" stopColor="#f0a050" />
          </linearGradient>
        </defs>
      </svg>

      <style>{`@keyframes loading-spin { to { transform: rotate(360deg); } }`}</style>
    </span>
  );

  if (inline) return spinner;

  return (
    <div style={{ display: "flex", justifyContent: "center", alignItems: "center", padding: "1.5rem 0", width: "100%" }}>
      {spinner}
    </div>
  );
};

export default Loading;